import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import toast from "react-hot-toast";

import useStyles from "./styles";
import {
  AuditLog,
  actionLabel,
  severityColor,
  useAuditLog,
} from "../../api/audit";

/**
 * Detail view for a single audit entry.
 *
 * A drawer rather than a route: the operator is scanning a table and wants to
 * inspect one row and get back. A full page navigation loses their scroll
 * position and their filters, which is the whole context they built up.
 */

interface Props {
  logId: number | null;
  onClose: () => void;
  /** Lets a related/history entry swap the drawer's content in place. */
  onNavigate?: (id: number) => void;
}

/** Render any JSON scalar in a way that distinguishes null from "" from 0. */
const renderValue = (value: any, classes: Record<string, string>) => {
  if (value === "[redacted]") {
    return (
      <span className={`${classes.diffNew} ${classes.redacted}`}>
        •••••• (masqué)
      </span>
    );
  }

  if (value === null || value === undefined) {
    return <span className={classes.emptyValue}>—</span>;
  }

  if (value === "") {
    return <span className={classes.emptyValue}>(vide)</span>;
  }

  if (typeof value === "boolean") {
    return <span>{value ? "true" : "false"}</span>;
  }

  if (typeof value === "object") {
    return <span>{JSON.stringify(value)}</span>;
  }

  return <span>{String(value)}</span>;
};

export const AuditDetailDrawer: React.FC<Props> = ({
  logId,
  onClose,
  onNavigate,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { data, isLoading } = useAuditLog(logId);

  const log = data?.log;

  const copy = (text: string | null, label: string) => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    toast.success(t("audit.copied", { defaultValue: `${label} copié` }));
  };

  /*
   * Which fields to show in the diff.
   *
   * changed_fields is authoritative rather than Object.keys(new_values):
   * a redacted field appears in changed_fields (so the trail records THAT the
   * password changed) while carrying a masked value. Deriving the list from
   * the values would be equivalent here but would silently diverge the moment
   * the backend redacts by omission instead of by masking.
   */
  const fields = log?.changed_fields ?? [];

  return (
    <Drawer
      anchor="right"
      open={!!logId}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
    >
      {isLoading || !log ? (
        <Box className={classes.emptyState}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <div className={classes.drawerHeader}>
            <div style={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                {actionLabel(log.action)}
                {log.subject_label ? ` — ${log.subject_label}` : ""}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  size="small"
                  label={log.severity}
                  color={severityColor(log.severity)}
                />
                <Chip size="small" label={log.category} variant="outlined" />
                {log.auditable_label && (
                  <Chip
                    size="small"
                    label={log.auditable_label}
                    variant="outlined"
                  />
                )}
              </Box>
            </div>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </div>

          <div className={classes.drawerBody}>
            {/* ── Who / when / where ─────────────────────────────────── */}
            <div
              className={`${classes.sectionTitle} ${classes.sectionTitleFirst}`}
            >
              {t("audit.detailContext", "Contexte")}
            </div>

            <div className={classes.metaGrid}>
              <span className={classes.metaKey}>{t("audit.date", "Date")}</span>
              <span className={classes.metaValue}>
                {log.created_at
                  ? new Date(log.created_at).toLocaleString("fr-FR")
                  : "—"}
              </span>

              <span className={classes.metaKey}>
                {t("audit.actor", "Auteur")}
              </span>
              <span className={classes.metaValue}>
                {log.actor_name || log.actor_email || (
                  <span className={classes.emptyValue}>
                    {log.actor_type === "system"
                      ? t("audit.systemActor", "Système")
                      : t("audit.anonymous", "Anonyme")}
                  </span>
                )}
                {log.actor_email && log.actor_name ? ` (${log.actor_email})` : ""}
              </span>

              {log.actor_roles && (
                <>
                  <span className={classes.metaKey}>
                    {t("audit.roles", "Rôles")}
                  </span>
                  <span className={classes.metaValue}>{log.actor_roles}</span>
                </>
              )}

              {log.company_id && (
                <>
                  <span className={classes.metaKey}>
                    {t("audit.company", "Société")}
                  </span>
                  <span className={classes.metaValue}>#{log.company_id}</span>
                </>
              )}

              <span className={classes.metaKey}>{t("audit.ip", "IP")}</span>
              <span className={`${classes.metaValue} ${classes.mono}`}>
                {log.ip_address || "—"}
              </span>

              {log.endpoint && (
                <>
                  <span className={classes.metaKey}>
                    {t("audit.endpoint", "Endpoint")}
                  </span>
                  <span className={`${classes.metaValue} ${classes.mono}`}>
                    {log.method} {log.endpoint}
                  </span>
                </>
              )}

              {/* The request id is the join key between this entry and every
                  other row produced by the same click. Copyable because
                  pasting it into the filter is how an operator reconstructs a
                  full operation. */}
              <span className={classes.metaKey}>
                {t("audit.requestId", "Request ID")}
              </span>
              <span className={`${classes.metaValue} ${classes.monoSmall}`}>
                {log.request_id || "—"}
                {log.request_id && (
                  <Tooltip title={t("audit.copy", "Copier") as string}>
                    <IconButton
                      size="small"
                      onClick={() => copy(log.request_id, "Request ID")}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </span>
            </div>

            {log.message && (
              <Typography sx={{ mt: 2, fontSize: 14 }}>{log.message}</Typography>
            )}

            {/* ── The diff ───────────────────────────────────────────── */}
            {fields.length > 0 && (
              <>
                <div className={classes.sectionTitle}>
                  {t("audit.changes", "Modifications")} ({fields.length})
                </div>

                {/* Header row, so the two value columns are unambiguous. A
                    bare red/green pair is guessable but this is a compliance
                    screen — it should not need guessing. */}
                <div
                  className={classes.diffRow}
                  style={{ border: "none", paddingBottom: 0, marginBottom: 4 }}
                >
                  <span className={classes.metaKey}>
                    {t("audit.field", "Champ")}
                  </span>
                  <span className={classes.metaKey}>
                    {t("audit.before", "Avant")}
                  </span>
                  <span className={classes.metaKey}>
                    {t("audit.after", "Après")}
                  </span>
                </div>

                {fields.map((field) => (
                  <div className={classes.diffRow} key={field}>
                    <span className={classes.diffField}>{field}</span>
                    <span className={classes.diffOld}>
                      {renderValue(log.old_values?.[field], classes)}
                    </span>
                    <span className={classes.diffNew}>
                      {renderValue(log.new_values?.[field], classes)}
                    </span>
                  </div>
                ))}
              </>
            )}

            {/* ── Extra context ──────────────────────────────────────── */}
            {log.context && Object.keys(log.context).length > 0 && (
              <>
                <div className={classes.sectionTitle}>
                  {t("audit.context", "Détails")}
                </div>
                <pre className={classes.jsonBlock}>
                  {JSON.stringify(log.context, null, 2)}
                </pre>
              </>
            )}

            {/* ── Same request ───────────────────────────────────────
                This is what turns a confusing single row ("rattachement
                supprimé") into a readable story ("the operator archived the
                company, which detached four users"). */}
            {data?.related && data.related.length > 0 && (
              <>
                <div className={classes.sectionTitle}>
                  {t("audit.sameRequest", "Même opération")} (
                  {data.related.length})
                </div>
                {data.related.map((rel: AuditLog) => (
                  <div
                    key={rel.id}
                    className={classes.relatedItem}
                    onClick={() => onNavigate?.(rel.id)}
                  >
                    <Chip
                      size="small"
                      label={rel.severity}
                      color={severityColor(rel.severity)}
                    />
                    <span>{actionLabel(rel.action)}</span>
                    <span className={classes.monoSmall}>
                      {rel.subject_label} {rel.auditable_label ?? ""}
                    </span>
                  </div>
                ))}
              </>
            )}

            {/* ── This record's history ──────────────────────────────── */}
            {data?.history && data.history.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <div
                  className={`${classes.sectionTitle} ${classes.sectionTitleFirst}`}
                >
                  {t("audit.recordHistory", "Historique de cet enregistrement")}
                </div>
                {data.history.map((h: AuditLog) => (
                  <div
                    key={h.id}
                    className={classes.relatedItem}
                    onClick={() => onNavigate?.(h.id)}
                  >
                    <span className={classes.monoSmall}>
                      {h.created_at
                        ? new Date(h.created_at).toLocaleString("fr-FR")
                        : "—"}
                    </span>
                    <span>{actionLabel(h.action)}</span>
                    <span className={classes.monoSmall}>
                      {h.actor_name || h.actor_email || "—"}
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      )}
    </Drawer>
  );
};
